import React, {useContext} from "react";
import Card from "atoms/Cards/Card";
import {NavLink} from "react-router-dom";
import {store} from "../../store";
import format from "date-fns/format";
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from "@mui/icons-material/Close";
import KitesurfingOutlined from '@mui/icons-material/KitesurfingOutlined';
import HotelOutlined from '@mui/icons-material/HotelOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import {getEventTypeColour} from '../eventTypeColours';

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const CountIcon = ({ value, IconComponent, activeColor = '#666', tooltip, compact }) => {
  const isZero = Number(value) === 0;
  const color = isZero ? '#bbb' : activeColor;

  return (
    <Tooltip title={tooltip} arrow>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        color,
        flexShrink: compact ? 1 : 0,
        minWidth: 0,
        cursor: 'default'
      }}>
        <IconComponent sx={{ fontSize: compact ? 12 : 16, color, flexShrink: 0 }} />
        <b style={{color, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}>{value}</b>
      </div>
    </Tooltip>
  );
};

export const ListDate = ({label, opacity= 1, compact}) => {
  return (
    <div style={{
      fontSize: compact ? 11 : 14,
      fontWeight:'bold',
      padding:'8px 2px 10px',
      margin:0,
      opacity: opacity
    }}>{label}
    </div>
  );
}

const DeleteIcon = styled(CloseIcon)({
  transform: 'scale(0.9)',
  color: 'red',
  position: 'absolute',
  top: 3,
  right: 3,
  zIndex: 10,
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
});

const EventContainer = styled('div')({
  '&:hover': {
    '& .delete-icon': {
      opacity: 1,
    }
  }
});

const EventTitle = styled('div')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 'bold',
  [theme.breakpoints.down('md')]: {
    fontSize: 12
  }
}));

export const Event = ({event, onDelete, dateIndex, compact = false, agenda = false}) => {
  const {state} = useContext(store);
  const compactDetails = compact && !agenda;
  const daysInRow = 7;
  const dayDistance = event.dayDistance - event.day;
  const endOfRow = ((dateIndex + 1) % daysInRow) === 0;
  const daySpan = Math.min(dayDistance, (endOfRow ?  1 : (8 - (dateIndex + 1) % daysInRow)));
  const isVisibleCard = agenda || event.start || (dateIndex % daysInRow === 0);
  const isLastRenderedSegment = daySpan === dayDistance;
  const endsToday = new Date(event.endDate).toDateString() === new Date().toDateString();
  const cornerBackground = endsToday ? '#e8f2e9' : '#f7fbfd';
  const background = getEventTypeColour(state.site, event.type);
  const hasKite = hasValue(event.kite) && Number(event.kite) > 0;
  const hasSleepers = hasValue(event.sleepers) && Number(event.sleepers) > 0;
  const showDepartureCorner = !agenda && isVisibleCard && hasSleepers && isLastRenderedSegment;
  const isProvisional = !!event.provisional;
  const eventType = isProvisional ? [event.type, '(provisional)'].filter(Boolean).join(' ') : event.type;
  const startTime = event.startDate ? format(new Date(event.startDate), 'h:mma') : '';
  const stripeColor = isProvisional ? '#f5a623' : (hasKite ? '#1866b8' : 'transparent');
  return (
    <Card onClick={()=>{}} style={{visibility:`${
      isVisibleCard ? 'visible' : 'hidden'}`,
      width: '100%',
      boxSizing: 'border-box',
      ...(compact ? {padding: agenda ? 5 : 3} : {padding: 8}),
      minWidth: 0,
      background: `${background}`,
      ...(stripeColor !== 'transparent' ? {
        borderLeft: `3px solid ${stripeColor}`,
        ...(!compact ? {paddingLeft: 6} : {})
      } : {})
    }}>
      <NavLink to={`/${state.site.normalName}/events/view/${event._id}`} style={{textDecoration:'none', color:'#333'}}>
      <EventContainer>
        {isVisibleCard && onDelete && <DeleteIcon className="delete-icon" fontSize={'small'} onClick={(e) => onDelete(e, event)}/> }
        <div style={{
          paddingBottom:0,
          fontSize: compact ? 10 : 15,
          display:'flex',
          flexDirection:'column',
          justifyContent: 'space-between',
          gap:4,
          position:'relative'
        }}>
          <Tooltip title={event.groupName} arrow>
            <EventTitle style={{
              whiteSpace: agenda ? 'normal' : 'nowrap',
              overflowWrap: agenda ? 'anywhere' : undefined,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              ...(compact ? {fontSize: agenda ? 12 : 10} : {})
            }}>{event.groupName}</EventTitle>
          </Tooltip>

          {(eventType || startTime) && (
            <div style={{
              fontSize: compactDetails ? 9 : 11,
              color: isProvisional ? '#b36a00' : '#666',
              whiteSpace: agenda ? 'normal' : 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              <b>{startTime && <span style={{color: '#2e7d32'}}>{startTime}</span>}
                {startTime && eventType ? ' · ' : ''}{eventType}</b>
            </div>
          )}

          {agenda && event.dayDistance > 1 && <div style={{fontSize: 11, color: '#666'}}>
            {event.day > 0 ? 'Continued · ' : ''}{format(new Date(event.startDate), 'd MMM')} – {format(new Date(event.endDate), 'd MMM')}
          </div>}

          {(hasValue(event.kite) || hasValue(event.sleepers) || hasValue(event.dayGuests)) && (
            <div style={{
              paddingBottom:0,
              marginTop:0,
              fontSize:compactDetails ? 9 : 11,
              whiteSpace: 'nowrap',
              overflow: 'hidden'
            }}>
              <div style={{
                display: 'flex',
                gap:compactDetails ? 3 : 10,
                alignItems: 'center',
              color: '#666',
              marginTop: 4,
              flexWrap: 'wrap',
              overflow: 'hidden'
            }}>
              {hasValue(event.kite) && (
                <CountIcon value={event.kite} IconComponent={KitesurfingOutlined} activeColor={'#1866b8'} tooltip={'Kites'} compact={compactDetails} />
              )}
              {hasValue(event.sleepers) && <CountIcon value={event.sleepers} IconComponent={HotelOutlined} tooltip={'Sleepers'} compact={compactDetails} />}
              {hasValue(event.dayGuests) && <CountIcon value={event.dayGuests} IconComponent={PersonOutlineOutlined} tooltip={'Day guests'} compact={compactDetails} />}
            </div>
          </div>
          )}

          {showDepartureCorner && (
            <div style={{
              position: 'absolute',
              right: compact ? -3 : -12,
              bottom: compact ? -3 : -12,
              width: compact ? 12 : 28,
              height: compact ? 12 : 28,
              pointerEvents: 'none'
            }}>
              <svg width="100%" height="100%" viewBox="0 0 28 28" aria-hidden="true">
                <polygon points="28,28 0,28 28,0" fill={cornerBackground} />
                <line x1="1" y1="27" x2="26" y2="1" stroke="#ccc" strokeWidth="1" />
              </svg>
            </div>
          )}
        </div>
      </EventContainer>
      </NavLink>
    </Card>
  );
}
